import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { LayoutGrid, Users, TrendingUp, Star } from 'lucide-react';
import GaugeMeter from '../../components/ui/GaugeMeter';
import Pagination from '../../components/ui/Pagination';
import { trainerService } from '../../services';
import {
  getMetricBandInfo,
  getTEIInterpretation,
  getLAIInterpretation,
} from '../../utils/reportingConstants';
import { formatLocalDate, formatActualDateTime } from '../../utils/dateUtils';

/**
 * Trainer Analytics Page
 * Comprehensive analytics dashboard for trainers
 */

const TrainerAnalyticsPage = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, all
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await trainerService.getAnalytics({ period: timeRange });
      if (response.success) {
        setAnalyticsData(response.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use API data or defaults - cap engagement at 100%
  const rawAnalytics = analyticsData?.metrics || {
    totalSessions: 0,
    completedSessions: 0,
    liveSessions: 0,
    totalParticipants: 0,
    avgEngagement: 0,
    avgRating: 0,
    totalHours: 0,
  };
  const analytics = {
    ...rawAnalytics,
    avgEngagement: Math.min(100, rawAnalytics.avgEngagement || 0),
  };

  // Session performance data from API - cap engagement at 100%
  const sessionPerformance = (analyticsData?.sessionPerformance || []).map(session => ({
    ...session,
    engagement: Math.min(100, session.engagement || 0),
  }));

  // Pagination for session performance table
  const totalPages = Math.ceil(sessionPerformance.length / itemsPerPage);
  const paginatedSessionPerformance = sessionPerformance.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when time range or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [timeRange, itemsPerPage]);

  // Engagement trends from API or defaults - cap engagement at 100%
  const engagementTrends = (analyticsData?.trends || [
    { week: 'Week 1', engagement: 0, participants: 0 },
    { week: 'Week 2', engagement: 0, participants: 0 },
    { week: 'Week 3', engagement: 0, participants: 0 },
    { week: 'Week 4', engagement: 0, participants: 0 },
  ]).map(trend => ({
    ...trend,
    engagement: Math.min(100, trend.engagement || 0),
  }));

  // Recent feedback from API
  const recentFeedback = analyticsData?.recentFeedback || [];

  return (
    <DashboardLayout
      title="Analytics Dashboard"
    >
      <div className="p-4 space-y-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/trainer/dashboard')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        {/* Header with Time Range Filter */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-secondary-900">
              Performance Analytics
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Track your training performance and participant engagement
            </p>
          </div>
          <div className="relative">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="appearance-none bg-white border border-neutral-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-neutral-700 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors cursor-pointer shadow-sm"
            >
              <option value="7d">Last 7d</option>
              <option value="30d">Last 30d</option>
              <option value="90d">Last 90d</option>
              <option value="all">All Time</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-neutral-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Total Sessions" value={analytics.totalSessions}        icon={LayoutGrid} variant="blue" />
          <StatCard label="Participants"   value={analytics.totalParticipants}    icon={Users}      variant="emerald" />
          <StatCard label="Avg Engagement" value={`${analytics.avgEngagement}%`}  icon={TrendingUp} variant="amber" />
          <StatCard label="Avg Rating"     value={analytics.avgRating}            icon={Star}       variant="violet" />
        </div>

        {/* Trainer Performance Indices */}
        <div className="bg-white rounded-2xl border border-neutral-100 p-5">
          <div className="mb-4">
            <h3 className="text-sm sm:text-base font-bold text-neutral-900">
              Performance Indices
            </h3>
            <p className="text-sm text-neutral-500 mt-0.5">
              Aggregated metrics across all your sessions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Trainer Effectiveness Index */}
            <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
              <GaugeMeter
                value={analyticsData?.indices?.trainerEffectiveness || analytics.avgRating || 0}
                maxValue={5}
                label="Trainer Effectiveness"
                band={getMetricBandInfo('tei', analyticsData?.indices?.trainerEffectiveness || analytics.avgRating || 0)?.label || 'N/A'}
                color={getMetricBandInfo('tei', analyticsData?.indices?.trainerEffectiveness || analytics.avgRating || 0)?.color || 'gray'}
                size="sm"
              />
              {(analyticsData?.indices?.trainerEffectiveness || analytics.avgRating) > 0 && (
                <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed">
                  {getTEIInterpretation(analyticsData?.indices?.trainerEffectiveness || analytics.avgRating, 'trainer')}
                </p>
              )}
            </div>

            {/* Learning Advocacy Index */}
            <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
              <GaugeMeter
                value={analyticsData?.indices?.learningAdvocacy || 0}
                maxValue={5}
                label="Learning Advocacy"
                band={getMetricBandInfo('lai', analyticsData?.indices?.learningAdvocacy || 0)?.label || 'N/A'}
                color={getMetricBandInfo('lai', analyticsData?.indices?.learningAdvocacy || 0)?.color || 'gray'}
                size="sm"
              />
              {analyticsData?.indices?.learningAdvocacy > 0 && (
                <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed">
                  {getLAIInterpretation(analyticsData?.indices?.learningAdvocacy, 'trainer')}
                </p>
              )}
            </div>

            {/* Total Challenges */}
            <div className="flex flex-col items-center justify-center p-4 bg-neutral-50 rounded-xl">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-2">
                <span className="text-xl sm:text-2xl font-bold text-orange-600">
                  {analyticsData?.indices?.totalChallenges || 0}
                </span>
              </div>
              <p className="text-sm font-medium text-neutral-700">Total Challenges</p>
              <p className="text-xs text-neutral-500">Captured in sessions</p>
            </div>

            {/* Total Action Plans */}
            <div className="flex flex-col items-center justify-center p-4 bg-neutral-50 rounded-xl">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <span className="text-xl sm:text-2xl font-bold text-green-600">
                  {analyticsData?.indices?.totalActionPlans || 0}
                </span>
              </div>
              <p className="text-sm font-medium text-neutral-700">Action Plans</p>
              <p className="text-xs text-neutral-500">Created by participants</p>
            </div>
          </div>
        </div>

        {/* Engagement Trends Bar Chart */}
        {engagementTrends.length > 0 && engagementTrends.some(t => t.engagement > 0) && (() => {
          const totalParticipants = engagementTrends.reduce((sum, t) => sum + (t.participants || 0), 0);
          const avgEng = engagementTrends.filter(t => t.engagement > 0).length > 0
            ? Math.round(engagementTrends.reduce((sum, t) => sum + t.engagement, 0) / engagementTrends.filter(t => t.engagement > 0).length)
            : 0;
          const bestWeek = engagementTrends.reduce((best, t) => t.engagement > best.engagement ? t : best, engagementTrends[0]);
          return (
            <div className="bg-white rounded-2xl border border-neutral-100 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-500 flex items-center justify-center">
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-neutral-900">Engagement Trends</span>
                </div>
                <span className="text-xs font-medium text-neutral-500 px-3 py-1.5 bg-neutral-100 rounded-lg">
                  Weekly
                </span>
              </div>
              <div className="mb-1">
                <span className="text-2xl sm:text-3xl font-bold text-neutral-900">{avgEng}%</span>
              </div>
              <div className="flex items-center gap-2 mb-5">
                {bestWeek.engagement > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                    {bestWeek.week} &#8599;
                  </span>
                )}
                <span className="text-xs text-neutral-500">
                  {totalParticipants} participant{totalParticipants !== 1 ? 's' : ''} total
                </span>
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={engagementTrends} barCategoryGap="25%" margin={{ top: 20, right: 5, left: 5, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trainerEngBarGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.15} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 12, fill: '#9CA3AF', fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '12px',
                        border: '1px solid #F3F4F6',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        padding: '8px 12px',
                      }}
                      formatter={(value, name, props) => {
                        const participants = props.payload?.participants || 0;
                        return [`${value}% (${participants} participant${participants !== 1 ? 's' : ''})`, 'Avg Engagement'];
                      }}
                      cursor={{ fill: 'transparent' }}
                    />
                    <Bar
                      dataKey="engagement"
                      fill="url(#trainerEngBarGradient)"
                      radius={[8, 8, 4, 4]}
                      maxBarSize={40}
                    >
                      <LabelList
                        dataKey="engagement"
                        position="top"
                        formatter={(val) => val > 0 ? `${val}%` : ''}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#10b981' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })()}

        {/* Top Performing Sessions */}
        {sessionPerformance.length > 0 && (
          <div className="bg-white rounded-2xl border border-neutral-100 p-5">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-500 flex items-center justify-center">
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-medium text-neutral-900">Top Performing Sessions</span>
                <p className="text-xs text-neutral-500">Ranked by engagement rate</p>
              </div>
            </div>

            <div className="space-y-2">
              {sessionPerformance
                .sort((a, b) => (b.engagement || 0) - (a.engagement || 0))
                .slice(0, 5)
                .map((session, index) => (
                  <div
                    key={session.id}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl hover:bg-neutral-100/80 transition-colors"
                  >
                    <div className="flex items-center justify-center w-7 h-7 bg-primary-100 text-primary-700 font-bold rounded-full text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 truncate">{session.name}</p>
                      <p className="text-xs text-neutral-500">
                        {formatLocalDate(session.date)}
                        {' \u2022 '}{session.participants || 0} participants
                        {session.rating ? ` \u2022 ${session.rating}/5.0` : ''}
                      </p>
                    </div>
                    <Badge
                      variant={session.engagement >= 90 ? 'success' : session.engagement >= 75 ? 'info' : 'warning'}
                    >
                      {session.engagement}%
                    </Badge>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Session Performance Table */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          <div className="p-5 border-b border-neutral-100">
            <h3 className="text-sm sm:text-base font-bold text-neutral-900">
              Recent Session Performance
            </h3>
            <p className="text-sm text-neutral-500 mt-0.5">
              Detailed metrics for your recent sessions
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">
                    Session Name
                  </th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">
                    Date
                  </th>
                  <th className="text-center py-3.5 px-5 text-sm font-medium text-neutral-500">
                    Participants
                  </th>
                  <th className="text-center py-3.5 px-5 text-sm font-medium text-neutral-500">
                    Engagement
                  </th>
                  <th className="text-center py-3.5 px-5 text-sm font-medium text-neutral-500">
                    Completion
                  </th>
                  <th className="text-center py-3.5 px-5 text-sm font-medium text-neutral-500">
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedSessionPerformance.map((session) => (
                  <tr
                    key={session.id}
                    className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors"
                  >
                    <td className="py-4 px-5">
                      <p className="text-sm font-medium text-neutral-900">
                        {session.name}
                      </p>
                    </td>
                    <td className="py-4 px-5 text-sm text-neutral-600">
                      {formatLocalDate(session.date)}
                    </td>
                    <td className="py-4 px-5 text-center text-sm text-neutral-900">
                      {session.participants}
                    </td>
                    <td className="py-4 px-5 text-center">
                      <Badge
                        variant={
                          session.engagement >= 90
                            ? 'success'
                            : session.engagement >= 75
                            ? 'info'
                            : 'warning'
                        }
                      >
                        {session.engagement}%
                      </Badge>
                    </td>
                    <td className="py-4 px-5 text-center">
                      {session.completion ? (
                        <Badge variant="success">{session.completion}%</Badge>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="py-4 px-5 text-center">
                      {session.rating ? (
                        <div className="flex items-center justify-center gap-1">
                          <svg
                            className="w-4 h-4 text-amber-400 fill-current"
                            viewBox="0 0 24 24"
                          >
                            <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                          <span className="text-sm font-medium text-neutral-900">
                            {session.rating}
                          </span>
                        </div>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {sessionPerformance.length > 0 && (
            <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={sessionPerformance.length}
              />
            </div>
          )}
        </div>

        {/* Recent Feedback */}
        <div className="bg-white rounded-2xl border border-neutral-100 p-5">
          <div className="mb-4">
            <h3 className="text-sm sm:text-base font-bold text-neutral-900">
              Recent Participant Feedback
            </h3>
            <p className="text-sm text-neutral-500 mt-0.5">
              What participants are saying about your sessions
            </p>
          </div>

          <div className="space-y-3">
            {recentFeedback.length > 0 ? (
              recentFeedback.map((feedback) => (
                <div
                  key={feedback.id}
                  className="p-4 border border-neutral-100 rounded-xl"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {feedback.participantName?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">
                          {feedback.participantName || 'Anonymous'}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {feedback.sessionName}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`w-4 h-4 ${
                            star <= feedback.rating
                              ? 'text-amber-400 fill-current'
                              : 'text-neutral-300'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                          />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-neutral-700">{feedback.comments}</p>
                  {feedback.submittedAt && (
                    <p className="text-xs text-neutral-500 mt-2">
                      {formatActualDateTime(feedback.submittedAt)}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <p>No feedback received yet</p>
                <p className="text-sm mt-1">
                  Feedback will appear here after participants complete your
                  sessions
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Insights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Training Hours */}
          <div className="bg-white rounded-2xl border border-neutral-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-500">
                  Total Training Hours
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-neutral-900 mt-2">
                  {analytics.totalHours.toFixed(1)}
                </p>
                <p className="text-sm text-neutral-500 mt-1">
                  Across {analytics.completedSessions} sessions
                </p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-xl">
                <svg
                  className="w-8 h-8 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-white rounded-2xl border border-neutral-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-500">
                  Session Success Rate
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-neutral-900 mt-2">
                  {analytics.completedSessions > 0
                    ? Math.round(
                        (analytics.completedSessions /
                          analytics.totalSessions) *
                          100
                      )
                    : 0}
                  %
                </p>
                <p className="text-sm text-neutral-500 mt-1">
                  {analytics.completedSessions} of {analytics.totalSessions}{' '}
                  completed
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainerAnalyticsPage;
